import KpiChart from "@app/components/kpi-chart";
import { useState } from "react";

// One stable colour per cohort — the colour stays tied to a cohort even when
// others are toggled off (we index by the cohort's position, not the visible
// position).
const COHORT_COLORS = ["#2563eb", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#0d9488"];

type Curve = {
  readonly label: string;
  readonly size: number;
  readonly points: ReadonlyArray<{ readonly month: string; readonly value: number | null | undefined }>;
};

// A multi-cohort curve chart with a per-cohort show/hide checkbox row — the
// shared surface for the cohort-analysis KPIs (ARPS, paid churn, CAC payback,
// plan-to-plan conversion). echarts merges options on update, so dropping a
// series from the array does NOT clear it (the known series-removal bug); we
// remount the chart via `key` whenever the visible set changes — the documented
// fix — so toggling a cohort cleanly adds/removes its line.
export default function CohortCurveChart({
  title,
  curves,
  format,
  maxCohorts = 5,
  hint,
  referenceLine,
}: {
  title: string;
  curves: readonly Curve[];
  format: "money" | "percent" | "number";
  maxCohorts?: number;
  hint?: string;
  referenceLine?: { value: number; label?: string };
}) {
  // The most recent N cohorts (curves arrive sorted oldest-first).
  const cohorts = curves.slice(-maxCohorts);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  const colorOf = (label: string) =>
    COHORT_COLORS[Math.max(0, cohorts.findIndex((c) => c.label === label)) % COHORT_COLORS.length];

  const toggle = (label: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  if (cohorts.length === 0) return null;
  const visible = cohorts.filter((c) => !hidden.has(c.label));

  return (
    <div className="space-y-1">
      {visible.length > 0 ? (
        <KpiChart
          key={visible.map((c) => c.label).join(",")}
          title={title}
          series={visible.map((c) => ({
            name: `${c.label} (n=${c.size})`,
            points: c.points,
            type: "line" as const,
            color: colorOf(c.label),
          }))}
          format={format}
          referenceLine={referenceLine}
          onMonthClick={() => {}}
        />
      ) : (
        <div className="bg-white rounded border p-4">
          <p className="font-medium text-sm text-gray-700">{title}</p>
          <p className="text-sm text-gray-400 mt-6 mb-6 text-center">All cohorts hidden — check one below to show its curve.</p>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-2">
        {cohorts.map((c) => (
          <label key={c.label} className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
            <input type="checkbox" checked={!hidden.has(c.label)} onChange={() => toggle(c.label)} />
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: colorOf(c.label) }} />
            <span className="text-gray-700">{c.label}</span>
            <span className="text-gray-400">n={c.size}</span>
          </label>
        ))}
      </div>
      {hint && <p className="text-[11px] text-gray-400 px-2">{hint}</p>}
    </div>
  );
}
